import type { SeriesArearangeOptions, SeriesLineOptions } from "highcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../api/useSetups";
import t from "../../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { lightest } from "@ui";
import { getAxisId } from "../../Chart/hooks/useQuantityYAxes";
import type { GapPointMetadata } from "../../Chart/PointMetadata";
import { GAP_POINT_METADATA } from "../../Chart/PointMetadata";
import getAnalysisFlagSerie from "../../Chart/series/getAnalysisFlagSerie";
import getMeasureSeries from "../../Chart/series/getMeasureSeries";
import getQuantityFlagSerie from "../../Chart/series/getQuantityFlagSerie";
import seriesColors from "../../Chart/series/seriesColors";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import get2SigmaSerie from "../get2SigmaSerie";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import getQuantityOffset from "../../../../api/getQuantityOffset";
import type { Project } from "../../../../api/useProjects";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { QuantityIndex, Quantity } from "../../../../api/useQuantities";
import { isAnalysisQuantityValid } from "../../metrology";
export const EXPECTED_SERIE_ID = "expected";

function getExpectedSerie(
  analyses: ActiveAnalysis[],
  quantity: Quantity,
  setups: Setup[],
  _metrologyOverrides: MetrologyOverrides,
  displayInvalidPoints: boolean,
): Highcharts.SeriesLineOptions {
  const points = analyses.reduce<Highcharts.PointOptionsObject[]>((pointArray, analysis, index) => {
    const previousAnalysis = analyses[index - 1];
    const { quantityValues, date } = analysis;
    const quantityValue = quantityValues[quantity.id];

    if (!quantityValue) {
      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        human: null,
        group: null,
        label: null,
        isSymptom: null,
      };
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: date,
        y: null,
        custom,
      });
      return pointArray;
    }

    const valid = isAnalysisQuantityValid(analysis, quantity);

    if (!displayInvalidPoints && !valid) {
      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        human: null,
        group: null,
        label: null,
        isSymptom: null,
      };
      pointArray.push({
        id: `analysis-id-${analysis.id}-invalid-hidden`,
        x: date,
        y: null,
        custom,
      });
      return pointArray;
    }

    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        human: null,
        group: null,
        label: null,
        isSymptom: null,
      };
      pointArray.push({
        id: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        x: (date + previousAnalysis.date) / 2,
        allowPointSelect: false,
        y: null,
        custom,
      });
    }

    const analysisSetup = getAnalysisSetup(setups, analysis);
    const offset = getQuantityOffset(analysisSetup, quantity.id);

    if (quantityValue.simulated === null) {
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: date,
        y: null,
      });
    } else {
      const expected: number | null = quantityValue.simulated + offset;

      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        human:
          expected === null
            ? null
            : {
                value: expected,
                unit: quantity.unit,
                precision: 2,
                sign: null,
              },
        group: analysis.group,
        label: analysis.label,
        isSymptom: null,
      };

      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: date,
        y: expected,
        custom,
        description: t("symptoms.chart.tooltip.description.expected"),
      });
    }

    return pointArray;
  }, []);

  return {
    id: EXPECTED_SERIE_ID,
    name: quantity.name,
    data: points,
    color: seriesColors.expected,
    allowPointSelect: false,
    yAxis: getAxisId(quantity, false, false),
    type: "line",
    replicateInNavigator: true,
    marker: {
      symbol: "circle",
      fillColor: lightest(seriesColors.expected),
      lineColor: seriesColors.expected,
      lineWidth: 2,
    },
  };
}
function getSeries(
  project: Project,
  analyses: ActiveAnalysis[],
  quantityIndex: QuantityIndex,
  selectedQuantity: SelectedQuantity,
  actionAllowed: boolean,
  setups: Setup[],
  activationFilters: {
    quantityId: string;
    min: number | null;
    max: number | null;
    maxStd: number | null;
  }[],
  publicationFlagDisplayed: boolean,
  metrologyOverrides: MetrologyOverrides,
  displayInvalidPoints: boolean,
): (SeriesLineOptions | SeriesArearangeOptions | Highcharts.SeriesScatterOptions)[] {
  const selectedQuantities = [selectedQuantity] as NonEmptyArray<SelectedQuantity>;

  const [measureSerie] = getMeasureSeries({
    analyses,
    selectedQuantities,
    axisGroupedByUnit: false,
    actionAllowed,
    symptomMode: true,
    activationFilters,
    selectedChartOption: "value",
    metrologyOverrides,
    displayInvalidPoints,
    quantityIndex,
  });
  const quantityFlagSerie = getQuantityFlagSerie({
    analyses,
    quantities: [selectedQuantity.quantity],
    withSymptomFlags: true,
    chartType: "amplitude",
    setups,
    metrologyOverrides,
    displayInvalidPoints,
  });
  const analysisFlagSerie = getAnalysisFlagSerie({
    project,
    setups,
    analyses,
    quantityIndex,
    chartType: "SYMPTOMS",
    publicationFlagDisplayed,
  });
  const expectedSerie = getExpectedSerie(
    analyses,
    selectedQuantity.quantity,
    setups,
    metrologyOverrides,
    displayInvalidPoints,
  );
  const sigmaSerie = get2SigmaSerie({
    analyses,
    selectedQuantities: [selectedQuantity],
    isGap: false,
    normalized: false,
    setups,
    metrologyOverrides,
    displayInvalidPoints,
  });

  return [quantityFlagSerie, measureSerie, expectedSerie, sigmaSerie, analysisFlagSerie];
}

export default getSeries;
